import { Button, IconButton } from '@zep-us/zus';
import { ResetIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { cn } from '@/utils/tailwind.util';
import { type QuizBankFilterOption } from '../types';
import { QuizBankGradeFilter } from './QuizBankGradeFilter';
import { QuizBankTypeFilter } from './QuizBankTypeFilter';
import { QuizBankUnitFilter } from './QuizBankUnitFilter';
interface QuizBankFilterPanelProps {
  gradeOptions: QuizBankFilterOption[];
  unitOptions: QuizBankFilterOption[];
  typeOptions: QuizBankFilterOption[];
  /** 단원이 선택돼 있으면 학년 카운트를 비활성 근거로 쓰지 않는다 */
  unitFilterApplied?: boolean;
  onSelectGrade: (gradeId: string) => void;
  onToggleUnit: (unitId: string) => void;
  onToggleType: (type: string) => void;
  className?: string;
  /** 모바일에서 필터 화면을 닫고 결과로 돌아간다. 태블릿 이상은 넘기지 않는다 */
  onDone?: () => void;
  /** 모바일 마무리 버튼에 보여줄 결과 수 */
  resultCount?: number;
  onReset: () => void;
}

/** 좌측 필터 패널 — 학년(radio) · 단원(checkbox) · 문제 유형. 모바일은 화면 전체를 차지한다. */
export const QuizBankFilterPanel = ({
  gradeOptions,
  unitOptions,
  typeOptions,
  unitFilterApplied,
  onSelectGrade,
  onToggleUnit,
  onToggleType,
  onReset,
  onDone,
  resultCount = 0,
  className
}: QuizBankFilterPanelProps) => {
  const {
    t
  } = useTranslation(['build']);
  return <aside className={cn('flex h-full min-h-0 w-full flex-col', 'tablet-sm:w-[300px] tablet-sm:shrink-0 tablet-sm:border-r tablet-sm:border-alternative', className)} data-sentry-component="QuizBankFilterPanel" data-sentry-source-file="QuizBankFilterPanel.tsx">
      {/* 헤더는 스크롤 밖 — 스크롤 영역을 본문으로 좁힌다 */}
      <div className="flex shrink-0 items-center gap-xs border-b border-assistive bg-default py-xs pl-lg pr-md">
        <h2 className="text-body-md-bold text-default">
          {t('build:quizBank.filter.panelTitle')}
        </h2>
        <IconButton className="ml-auto" variant="transparent" intent="neutral" size="xs" aria-label={t('build:quizBank.filter.reset')} onClick={onReset} data-sentry-element="IconButton" data-sentry-source-file="QuizBankFilterPanel.tsx">
          <ResetIcon size="sm" data-sentry-element="ResetIcon" data-sentry-source-file="QuizBankFilterPanel.tsx" />
        </IconButton>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-lg overflow-y-auto px-md pb-lg pt-md">
        {/* 선택지가 없는 facet 은 제목만 남은 빈 섹션이 되므로 마운트하지 않는다 */}
        {gradeOptions.length > 0 && <QuizBankGradeFilter options={gradeOptions} unitFilterApplied={unitFilterApplied} onSelect={onSelectGrade} />}
        {unitOptions.length > 0 && <QuizBankUnitFilter options={unitOptions} onToggleUnit={onToggleUnit} />}
        {typeOptions.length > 0 && <QuizBankTypeFilter options={typeOptions} onToggle={onToggleType} />}
      </div>

      {onDone && <div className="shrink-0 border-t border-alternative bg-default px-md py-sm tablet-sm:hidden">
          <Button className="w-full" size="lg" variant="contained" onClick={onDone}>
            {t('build:quizBank.filter.apply', {
          n: resultCount.toLocaleString()
        })}
          </Button>
        </div>}
    </aside>;
};